import { deleteField, type FieldValue } from "firebase/firestore";

import { DAY_TITLE_MAX, DAY_TITLES_MAX } from "../events/landing";

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/** A real calendar date in `yyyy-mm-dd` as a UTC midnight, or null. */
function dayStart(key: string): number | null {
  if (!DAY.test(key)) return null;
  const time = Date.parse(`${key}T00:00:00Z`);
  // 2026-02-30 parses in some engines by rolling over, so round-trip it.
  return Number.isNaN(time) || new Date(time).toISOString().slice(0, 10) !== key
    ? null
    : time;
}

/**
 * The days of the event, as the keys `dayTitles` uses, from the start date to
 * the end date inclusive and at most DAY_TITLES_MAX of them.
 *
 * The dates are calendar dates on the event's own clock, the same ones the
 * agenda groups sessions under, so counting them needs no time zone: stepping
 * a UTC midnight by 24 hours cannot skip or repeat a day the way stepping a
 * local time across a clock change can.
 */
export function eventDays(
  startDate: string,
  endDate: string,
): { days: string[]; truncated: boolean } {
  const start = dayStart(startDate);
  const end = dayStart(endDate);
  if (start === null || end === null || end < start)
    return { days: [], truncated: false };
  const count = Math.round((end - start) / DAY_MS) + 1;
  const days = Array.from({ length: Math.min(count, DAY_TITLES_MAX) }, (_, i) =>
    new Date(start + i * DAY_MS).toISOString().slice(0, 10),
  );
  return { days, truncated: count > DAY_TITLES_MAX };
}

export type DayTitlesForm = Record<string, string>;

/**
 * One text per day of the event: what the organiser has typed for that day, else
 * the stored title, else empty. Stored titles and edits for other days are not
 * here, so the form always has exactly the days the event has now.
 */
export function dayTitlesFormFrom(
  days: readonly string[],
  stored: Record<string, string> | undefined,
  edits: DayTitlesForm = {},
): DayTitlesForm {
  return Object.fromEntries(
    days.map((day) => [day, edits[day] ?? stored?.[day] ?? ""]),
  );
}

/**
 * The edits still worth keeping once the days or the stored titles have
 * changed: those for a day the event still has whose text differs from what is
 * stored. An edit for a day that went away is dropped, and one that now equals
 * the stored title (it was saved) is no longer an edit.
 */
export function settleDayTitleEdits(
  edits: DayTitlesForm,
  days: readonly string[],
  stored: Record<string, string> | undefined,
): DayTitlesForm {
  return Object.fromEntries(
    Object.entries(edits).filter(
      ([day, title]) => days.includes(day) && title !== (stored?.[day] ?? ""),
    ),
  );
}

/** Stored days the event no longer has; saving drops their titles. */
export function staleDayTitles(
  days: readonly string[],
  stored: Record<string, string> | undefined,
): string[] {
  return Object.keys(stored ?? {}).filter((day) => !days.includes(day));
}

/** A message per day whose title the rules' projection would drop. */
export function dayTitleProblems(form: DayTitlesForm): Record<string, string> {
  const problems: Record<string, string> = {};
  for (const [day, title] of Object.entries(form))
    if (title.trim().length > DAY_TITLE_MAX)
      problems[day] =
        `A day title must be ${DAY_TITLE_MAX} characters or fewer.`;
  return problems;
}

/**
 * The write that makes the stored titles what the form says, or null when
 * nothing changed. Empty titles are left out of the map, and with none left
 * the key is removed. Titles for days outside the event go with the write,
 * which is also what keeps the map under its 14 entries.
 */
export function dayTitlesChange(
  form: DayTitlesForm,
  days: readonly string[],
  stored: Record<string, string> | undefined,
): { dayTitles: Record<string, string> | FieldValue } | null {
  const next: Record<string, string> = {};
  for (const day of days) {
    const title = (form[day] ?? "").trim();
    if (title) next[day] = title;
  }
  // Both are in day order, so comparing the serialised maps is exact.
  const before = Object.fromEntries(
    Object.entries(stored ?? {}).sort(([a], [b]) => a.localeCompare(b)),
  );
  if (JSON.stringify(next) === JSON.stringify(before)) return null;
  return { dayTitles: Object.keys(next).length > 0 ? next : deleteField() };
}
